<template>
  <div class="chart-container">
    <loading
      :active.sync="isLoading"
      :is-full-page="true"
      loader="bars"
    ></loading>
    <SuccessModal
      :show="success"
      @close="handleClose"
      :msg="$t('changes_success')"
      :heading="$t('changes_saved')"
    />

    <form class="form" @submit.prevent="handleSave">
      <div class="input-box">
        <div class="flex-column d-flex w-100">
          <label>{{ $t("chart_date_time_setttings") }}</label>
          <select v-model="chartDateTimeSettings">
            <option value="transmissionStartDate">
              {{ $t("transmission_start_date") }}
            </option>
            <option value="last24Hours">{{ $t("last_24_hrs") }}</option>
          </select>
        </div>

        <div class="flex-column d-flex w-100">
          <label>{{ $t("chart_type") }}</label>
          <select v-model="chartType">
            <option value="old">
              {{ $t("single_axis") }}
            </option>
            <option value="new">{{ $t("multi-axis") }}</option>
          </select>
        </div>
      </div>

      <!-- Drag and Drop Parameter Checklist Section - Displayed only if Multi-Axis is selected -->
      <div class="parameter-section" v-if="chartType === 'new'">
        <label class="section-label">Chart Parameters & Display Order</label>
        <p class="section-desc">Select parameters to display on the chart and drag to rearrange their order.</p>

        <div class="draggable-list">
          <div
            v-for="(field, index) in parametersList"
            :key="field.key"
            class="draggable-item"
            :class="{ 
              'dragging': draggingIndex === index, 
              'unchecked': !field.checked,
              'disabled-item': field.key === 'temperature'
            }"
            :draggable="field.key !== 'temperature'"
            @dragstart="onDragStart(index, $event)"
            @dragover.prevent
            @dragenter="onDragEnter(index)"
            @dragend="onDragEnd"
            @drop="onDrop"
          >
            <div class="drag-handle" :class="{ 'disabled-handle': field.key === 'temperature' }">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="9" cy="5" r="1.5" fill="currentColor"/>
                <circle cx="9" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="9" cy="19" r="1.5" fill="currentColor"/>
                <circle cx="15" cy="5" r="1.5" fill="currentColor"/>
                <circle cx="15" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="15" cy="19" r="1.5" fill="currentColor"/>
              </svg>
            </div>

            <label class="checkbox-container" :class="{ 'disabled-label': field.key === 'temperature' }">
              <input 
                type="checkbox" 
                :disabled="field.key === 'temperature'" 
                v-model="field.checked" 
              />
              <span class="custom-checkbox"></span>
              <span class="param-name">
                {{ getParamLabel(field.key) }}
                <span class="default-badge" v-if="field.key === 'temperature'"> (Default)</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="error" v-if="error">{{ error }}</div>
      <div class="button-container">
        <button class="button" type="submit">
          {{ $t("save") }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import SuccessModal from "../Modals/SuccessModal.vue";

export default {
  name: "ChartSettings",
  data() {
    return {
      chartDateTimeSettings: "",
      isLoading: false,
      message: "",
      error: "",
      success: false,
      chartType: "old",
      parametersList: [
        { key: "temperature", checked: true },
        { key: "humidity", checked: true },
        { key: "vibration", checked: true },
        { key: "light", checked: true }
      ],
      draggingIndex: null,
    };
  },
  components: { Loading, SuccessModal },
  computed: {
    ...mapGetters(["profile"]),
  },
  methods: {
    ...mapActions(["handleChartUpdate", "fetchProfile"]),
    handleClose() {
      this.success = false;
    },
    initializeParametersList() {
      const defaultFields = [
        { key: "temperature", checked: true },
        { key: "humidity", checked: true },
        { key: "vibration", checked: true },
        { key: "light", checked: true }
      ];

      const savedFields = this.profile?.company?.chartFields;

      if (savedFields && Array.isArray(savedFields) && savedFields.length > 0) {
        const list = [];
        
        // Temperature is always the first item and checked
        list.push({ key: "temperature", checked: true });

        // Sort the remaining saved fields by their order
        const remainingSaved = [...savedFields]
          .filter(sf => sf.key !== "temperature")
          .sort((a, b) => a.order - b.order);

        // Add remaining saved fields
        remainingSaved.forEach(sf => {
          const match = defaultFields.find(df => df.key === sf.key);
          if (match) {
            list.push({ key: sf.key, checked: true });
          }
        });

        // Add remaining default fields that are missing in savedFields (unchecked)
        defaultFields.forEach(df => {
          if (!list.some(item => item.key === df.key)) {
            list.push({ key: df.key, checked: false });
          }
        });

        this.parametersList = list;
      } else {
        this.parametersList = defaultFields;
      }
    },
    getParamLabel(key) {
      const labels = {
        temperature: "Temperature",
        humidity: "Humidity",
        vibration: "Vibration",
        light: "Light"
      };
      return labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
    },
    onDragStart(index, event) {
      if (this.parametersList[index].key === "temperature") {
        event.preventDefault();
        return;
      }
      this.draggingIndex = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
      }
    },
    onDragEnter(index) {
      // Do not allow reordering into the first position (which belongs to temperature)
      if (index === 0 || this.draggingIndex === 0) {
        return;
      }
      if (this.draggingIndex !== null && this.draggingIndex !== index) {
        const draggedItem = this.parametersList[this.draggingIndex];
        this.parametersList.splice(this.draggingIndex, 1);
        this.parametersList.splice(index, 0, draggedItem);
        this.draggingIndex = index;
      }
    },
    onDragEnd() {
      this.draggingIndex = null;
    },
    onDrop() {
      this.draggingIndex = null;
    },
    handleSave() {
      const selectedFields = this.parametersList.filter(item => item.checked);
      if (this.chartType === "new" && selectedFields.length === 0) {
        this.error = "Please select at least one chart parameter.";
        return;
      }

      this.isLoading = true;
      this.error = "";

      const chartFields = selectedFields.map((field, index) => ({
        key: field.key,
        order: index + 1
      }));

      this.handleChartUpdate({
        chartDateTimeSettings: this.chartDateTimeSettings,
        chartType: this.chartType,
        chartFields: chartFields
      })
        .then((response) => {
          console.log(response);
          this.success = true;
          this.fetchProfile()
            .then(() => {
              this.isLoading = false;
              this.initializeParametersList();
            })
            .catch(() => {
              this.isLoading = false;
            });
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
  },
  mounted() {
    this.chartDateTimeSettings =
      this.profile?.company?.chartDateTimeSettings || "transmissionStartDate";
    this.chartType = this.profile?.company?.chartType || "old";
    this.initializeParametersList();

    this.fetchProfile().then(() => {
      this.chartDateTimeSettings =
        this.profile?.company?.chartDateTimeSettings || "transmissionStartDate";
      this.chartType = this.profile?.company?.chartType || "old";
      this.initializeParametersList();
    });
  },
};
</script>

<style scoped>
.chart-container {
  background-color: #fff;
  position: relative;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0px 3px 60px 0px #a3abb91a;
  max-width: 1000px;
  width: 100%;
  margin: auto;
  min-height: 400px;
}
.input-box {
  display: flex;
  gap: 15px;
  margin-bottom: 24px;
}
.input-box select {
  height: 45px;
  border-radius: 8px;
  padding: 6px 14px;
  box-sizing: border-box;
  color: #5e6a79;
  border: 1px solid #cbd5e1;
  background-color: #fff;
  width: 100%;
}
.input-box select:focus {
  outline: none;
  border-color: #213e7a;
}
.parameter-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}
.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  display: block;
}
.section-desc {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 16px;
}
.draggable-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.draggable-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  cursor: grab;
  user-select: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.draggable-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}
.draggable-item.dragging {
  opacity: 0.5;
  border: 2px dashed #213e7a;
  background: #eff6ff;
  transform: scale(0.98);
}
.draggable-item.unchecked {
  background: #f8fafc;
  opacity: 0.75;
}
.draggable-item.disabled-item {
  opacity: 0.6;
  background: #f1f5f9;
  border-color: #cbd5e1;
  cursor: not-allowed;
}
.draggable-item.disabled-item:hover {
  transform: none;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}
.drag-handle {
  display: flex;
  align-items: center;
  color: #94a3b8;
  margin-right: 12px;
  cursor: grab;
  transition: color 0.15s ease;
}
.drag-handle.disabled-handle {
  cursor: not-allowed;
  opacity: 0.3;
  pointer-events: none;
}
.draggable-item:active {
  cursor: grabbing;
}
.draggable-item.disabled-item:active {
  cursor: not-allowed;
}
.draggable-item:active .drag-handle {
  cursor: grabbing;
  color: #64748b;
}

/* Custom Checkbox Design */
.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  margin: 0;
}
.checkbox-container.disabled-label {
  cursor: not-allowed;
}
.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}
.custom-checkbox {
  height: 20px;
  width: 20px;
  background-color: #ffffff;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  margin-right: 12px;
  display: inline-block;
  position: relative;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
.checkbox-container:hover input:not(:disabled) ~ .custom-checkbox {
  border-color: #213e7a;
}
.checkbox-container input:checked ~ .custom-checkbox {
  background-color: #213e7a;
  border-color: #213e7a;
}
.checkbox-container input:disabled ~ .custom-checkbox {
  background-color: #94a3b8;
  border-color: #94a3b8;
  cursor: not-allowed;
}
.custom-checkbox::after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.checkbox-container input:checked ~ .custom-checkbox::after {
  display: block;
}
.param-name {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  transition: color 0.15s ease;
}
.checkbox-container input:checked ~ .param-name {
  color: #213e7a;
  font-weight: 600;
}
.checkbox-container input:disabled ~ .param-name {
  color: #64748b;
  cursor: not-allowed;
}
.default-badge {
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
  font-style: italic;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
.button {
  color: #fff;
  background-color: #213e7a;
  display: grid;
  place-items: center;
  border-radius: 12px;
  width: 90px;
  height: 45px;
  border: 0;
  outline: none;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
}
.button:hover {
  background-color: #1a3262;
}
.button:focus {
  outline: none;
}
.error {
  color: #ef4444;
  font-size: 12px;
  margin-bottom: 12px;
}

@media screen and (max-width: 768px) {
  .input-box {
    display: flex;
    flex-direction: column;
  }
}
</style>
